import React, { useState, useEffect } from 'react';
import { HvModal, HvButton } from 'components';
import { useDispatch } from 'react-redux';
import { Typography, makeStyles } from '@material-ui/core';
import { MODAL, COLOR } from 'config/constants';
import { hideModal, showModal } from 'state/actions';
import { useListing, useAiServices } from 'hooks';
import { ServiceOfferDetails } from 'models';
import { useIntl } from 'react-intl';
import {
  setListingPictures,
  initialize,
  setCanProceed,
  setCurrentStep,
  setListingId,
  setSelectedService,
  startNewDesign,
  setShowDisclaimer,
} from 'state/slices/aiServices';
import { loadServiceConfig } from './aiServiceSteps/stepsConfig';
import * as serviceSteps from './aiServiceSteps/index';

interface Props {
  selectedService: any;
  opened_from_ai_section?: boolean;
}

const useStyles = makeStyles({
  buttonBox: {
    paddingTop: 8,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  createButton: {
    backgroundColor: COLOR.WHITE,
    color: COLOR.LIGHT_BLUE,
    border: `1px solid ${COLOR.LIGHT_BLUE}`,
    '@media (max-width: 768px)': {
      fontSize: 10,
    },
  },
  leftButton: {
    backgroundColor: COLOR.WHITE,
    marginRight: 20,
    color: COLOR.LIGHT_BLUE,
    border: `1px solid ${COLOR.LIGHT_BLUE}`,
  },
  doneButton: {
    '@media (max-width: 768px)': {
      fontSize: 10,
    },
  },
});

function AiServices({ selectedService, opened_from_ai_section }: Props) {
  const dispatch = useDispatch();
  const intl = useIntl();
  const classes = useStyles();

  const onClose = () => {
    dispatch(hideModal(MODAL.AI_SERVICES));
    dispatch(initialize());
  };
  const path = window.location.pathname;
  const arr = path.split('/');
  const distributionToken = arr[arr.length - 1];
  const { listing } = useListing(distributionToken);
  useEffect(() => {
    dispatch(setSelectedService(selectedService));
  }, []);
  const { canProceed, noMoreSteps, currentStep, hideButtons, selectedRoomStyle, selectedRoomType } =
    useAiServices();
  const aiVirtualServices = listing.virtualServices.filter((service: ServiceOfferDetails) => {
    return service.is_ai_service;
  });

  const fmtNext = intl.formatMessage({ id: 'next' });
  const fmtBack = intl.formatMessage({ id: 'back' });
  const fmtAiServicesTitle = intl.formatMessage({ id: 'deliver.aiServices.Title' });
  const fmtGenerateButton = intl.formatMessage({ id: 'deliver.aiServices.generate' });
  const fmtMakeNewDesign = intl.formatMessage({ id: 'deliver.aiServices.makeNewDesign' });
  const fmtDone = intl.formatMessage({ id: 'done' });

  const handleServiceSelect = (service: ServiceOfferDetails) => {
    dispatch(setSelectedService(service));
    dispatch(setCurrentStep(0));
  };

  const handleNextStep = () => {
    dispatch(setCurrentStep(currentStep + 1));
    setCanProceed(false);
  };

  const handleBack = async () => {
    if (opened_from_ai_section) {
      dispatch(setSelectedService(null));
      dispatch(hideModal(MODAL.AI_SERVICES));
      dispatch(initialize());
    } else if (currentStep === 0) {
      dispatch(setSelectedService(null));
      dispatch(hideModal(MODAL.AI_SERVICES));
      dispatch(initialize());
      dispatch(showModal({ type: MODAL.VIRTUAL_STAGING }));
    } else {
      dispatch(setCurrentStep(currentStep - 1));
    }
  };

  useEffect(() => {
    if (listing && listing.pictures) {
      dispatch(setListingPictures(listing.pictures));
      dispatch(setListingId(listing.id));
    }
  }, [listing]);

  function modalButtons() {
    return (
      <>
        {currentStep < 2 ? (
          <HvButton className={currentStep === 1 ? classes.leftButton : null} onClick={handleBack}>
            {fmtBack}
          </HvButton>
        ) : null}
        {!noMoreSteps && currentStep < 2 && (
          <HvButton disabled={!canProceed} onClick={handleNextStep}>
            {fmtNext}
          </HvButton>
        )}
        {currentStep === 2 && (
          <HvButton
            onClick={() => {
              dispatch(startNewDesign());
            }}
            className={classes.createButton}
          >
            {fmtMakeNewDesign}
          </HvButton>
        )}
        {currentStep === 2 && (
          <HvButton
            className={classes.doneButton}
            onClick={() => {
              dispatch(hideModal(MODAL.AI_SERVICES));
              dispatch(initialize());
            }}
          >
            {fmtDone}
          </HvButton>
        )}
        {currentStep === 1 ? (
          <HvButton
            onClick={() => {
              dispatch(setShowDisclaimer(true));
            }}
            disabled={!selectedRoomStyle || !selectedRoomType}
          >
            {fmtGenerateButton}
          </HvButton>
        ) : null}
      </>
    );
  }

  function loadSteps() {
    const CurrentServiceStep =
      serviceSteps[loadServiceConfig(selectedService.id).steps[currentStep].component];
    return <CurrentServiceStep />;
  }

  const ServiceSelection = serviceSteps.ServiceSelectionStep;

  return (
    <HvModal
      id='aiServicesModal'
      maxWidth='md'
      headerImage='virtualStagingBg'
      title={fmtAiServicesTitle}
      onClose={onClose}
      buttons={selectedService && modalButtons()}
    >
      {!selectedService ? (
        <ServiceSelection
          onClose={onClose}
          services={aiVirtualServices}
          onServiceSelect={handleServiceSelect}
        />
      ) : (
        loadSteps()
      )}
    </HvModal>
  );
}

export default AiServices;
